import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Link, Text, type TextProps } from '@revelio/ui';

import styles from './markdown-renderer.module.css';

export type MarkdownRendererVariant = 'default' | 'assistant';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

const HEADING_SIZES: Record<
  HeadingLevel,
  NonNullable<TextProps['fontSize']>
> = {
  h1: 'x-lg',
  h2: 'lg',
  h3: 'x-bg',
  h4: 'bg',
  h5: 'md',
};

const MarkdownHeading = ({
  level,
  variant,
  className,
  ...props
}: Omit<TextProps, 'as' | 'fontSize' | 'weight'> & {
  level: HeadingLevel;
  variant: MarkdownRendererVariant;
}) => (
  <Text
    {...props}
    as={level}
    fontSize={variant === 'assistant' ? 'x-sm' : HEADING_SIZES[level]}
    weight={level === 'h1' ? 'bold' : 'semibold'}
    data-variant={variant}
    tone="inherit"
    className={classNames(styles.heading, styles[level], className)}
  />
);

export const MarkdownRenderer = ({
  content,
  variant = 'default',
}: {
  content: string | null;
  variant?: MarkdownRendererVariant;
}) => {
  const isAssistant = variant === 'assistant';

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node: _node, color: _color, ref: _ref, ...props }) => {
          const href = props.href || '';
          if (href.startsWith('http://') || href.startsWith('https://')) {
            return (
              <Link
                rel="noopener noreferrer"
                target="_blank"
                {...props}
                className={classNames(styles.link, props.className)}
              />
            );
          }
          return props.children;
        },
        h1: ({ node: _node, ...props }) => (
          <MarkdownHeading level="h1" variant={variant} {...props} />
        ),
        h2: ({ node: _node, ...props }) => (
          <MarkdownHeading level="h2" variant={variant} {...props} />
        ),
        h3: ({ node: _node, ...props }) => (
          <MarkdownHeading level="h3" variant={variant} {...props} />
        ),
        h4: ({ node: _node, ...props }) => (
          <MarkdownHeading level="h4" variant={variant} {...props} />
        ),
        h5: ({ node: _node, ...props }) => (
          <MarkdownHeading level="h5" variant={variant} {...props} />
        ),
        p: ({ node: _node, ...props }) => (
          <Text
            {...props}
            fontSize={isAssistant ? 'x-sm' : 'md'}
            data-variant={variant}
            className={classNames(styles.paragraph, props.className)}
          />
        ),
        ul: ({ node: _node, ...props }) => (
          <ul
            {...props}
            className={classNames(
              styles.unorderedList,
              isAssistant && styles.assistantList,
              props.className
            )}
          />
        ),
        ol: ({ node: _node, ...props }) => (
          <ol
            {...props}
            className={classNames(
              styles.orderedList,
              isAssistant && styles.assistantList,
              props.className
            )}
          />
        ),
        strong: ({ node: _node, ...props }) => (
          <Text {...props} as="strong" weight="bold" fontSize="inherit" />
        ),
        code: ({ node, ...props }) => {
          const isInline =
            node?.position?.start?.line === node?.position?.end?.line;
          return (
            <code
              data-inline={isInline || undefined}
              {...props}
              className={classNames(styles.code, props.className)}
            />
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
